import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { ProdStatusBadge } from "./Production";

const REJECT_REASONS = ["Bahan rusak", "Salah proses", "Kemasan rusak", "Produk tidak sesuai", "Lainnya"];

export default function ProductionPackages() {
  const [rows, setRows] = useState([]);
  const [selId, setSelId] = useState("");
  const [detail, setDetail] = useState(null);
  const [forms, setForms] = useState({});

  const load = useCallback(async () => {
    const r = await api.get("/productions");
    setRows(r.data.filter((x) => ["MATERIAL_ISSUED", "IN_PROGRESS", "WAITING_CHECK"].includes(x.status)));
  }, []);
  useEffect(() => { load().catch((e) => console.error(e)); }, [load]);

  const openProd = async (id) => {
    setSelId(id);
    if (!id) { setDetail(null); return; }
    const d = (await api.get(`/productions/${id}`)).data;
    setDetail(d);
    const f = {};
    d.packages.forEach((p) => { f[p.package_id] = { good: p.good_dus ?? p.target_dus, reject: p.reject_dus || 0, reason: p.reject_reason || "", note: p.note || "" }; });
    setForms(f);
  };

  const savePkg = async (pkg) => {
    const f = forms[pkg.package_id];
    try {
      const r = await api.put(`/productions/${detail.production_id}/packages/${pkg.package_id}`, {
        good_dus: Number(f.good), reject_dus: Number(f.reject), reject_reason: f.reason, note: f.note,
      });
      if (r.data.package_status === "SELISIH") toast.warning(`Paket #${pkg.seq}: masih ada dus yang belum dijelaskan`);
      else toast.success(`Paket #${pkg.seq} (${pkg.employee_name}) tersimpan — seimbang`);
      await openProd(selId);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const totalGood = detail ? detail.packages.reduce((a, p) => a + (p.good_dus || 0), 0) : 0;
  const totalReject = detail ? detail.packages.reduce((a, p) => a + (p.reject_dus || 0), 0) : 0;

  return (
    <div data-testid="page-production-packages" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Produksi</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Paket & Hasil Produksi</h1>
        <p className="text-slate-500 text-sm mt-1">Target dibagi otomatis seimbang per operator. Upah = GOOD × tarif (reject tidak dibayar).</p>
      </div>

      <Card>
        <CardContent className="py-4 flex items-end gap-3">
          <div className="w-96">
            <Label>Produksi Aktif</Label>
            <Select value={selId} onValueChange={openProd}>
              <SelectTrigger data-testid="input-package-production"><SelectValue placeholder="Pilih produksi" /></SelectTrigger>
              <SelectContent>{rows.map((r) => <SelectItem key={r.production_id} value={r.production_id}>{r.prod_no} · {r.product_name} ({num(r.target_dus)} dus)</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {detail && <ProdStatusBadge status={detail.status} />}
        </CardContent>
      </Card>

      {detail && (
        <>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="package-cards">
            {detail.packages.map((p) => {
              const f = forms[p.package_id] || { good: 0, reject: 0, reason: "", note: "" };
              const gap = p.target_dus - Number(f.good || 0) - Number(f.reject || 0);
              const set = (patch) => setForms({ ...forms, [p.package_id]: { ...f, ...patch } });
              return (
                <Card key={p.package_id} className={gap === 0 && p.good_dus != null ? "border-emerald-300" : gap !== 0 && p.good_dus != null ? "border-rose-300" : ""} data-testid={`package-card-${p.seq}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="font-display text-base flex items-center justify-between">
                      <span>Paket #{p.seq} — {p.employee_name}</span>
                      {p.status === "SELESAI" ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">Selesai</Badge>
                        : p.status === "SELISIH" ? <Badge className="bg-rose-100 text-rose-800 font-normal">Selisih</Badge>
                        : <Badge className="bg-slate-100 text-slate-500 font-normal">Pending</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Target</span><span className="font-mono font-bold">{p.target_dus} dus</span></div>
                    <div><Label className="text-xs">Good (dus)</Label><Input type="number" value={f.good} onChange={(e) => set({ good: e.target.value })} data-testid={`pkg-good-${p.seq}`} /></div>
                    <div><Label className="text-xs">Reject (dus)</Label><Input type="number" value={f.reject} onChange={(e) => set({ reject: e.target.value })} data-testid={`pkg-reject-${p.seq}`} /></div>
                    {Number(f.reject) > 0 && (
                      <div><Label className="text-xs">Alasan Reject</Label>
                        <Select value={f.reason} onValueChange={(v) => set({ reason: v })}>
                          <SelectTrigger data-testid={`pkg-reason-${p.seq}`}><SelectValue placeholder="Pilih alasan" /></SelectTrigger>
                          <SelectContent>{REJECT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    <div><Label className="text-xs">Keterangan</Label><Input value={f.note} onChange={(e) => set({ note: e.target.value })} data-testid={`pkg-note-${p.seq}`} /></div>
                    {gap !== 0 && (
                      <div className="text-xs text-amber-700 flex items-center gap-1" data-testid={`pkg-gap-${p.seq}`}>
                        <AlertTriangle className="h-3 w-3" /> Masih ada {gap} dus yang belum dijelaskan
                      </div>
                    )}
                    <div className="flex justify-between text-xs text-slate-500"><span>Upah (good × {rupiah(detail.wage_rate)})</span><span className="font-mono font-bold text-slate-800">{rupiah(Number(f.good || 0) * detail.wage_rate)}</span></div>
                    <Button className="w-full bg-slate-900 hover:bg-slate-800" size="sm" onClick={() => savePkg(p)} data-testid={`pkg-save-${p.seq}`}><Save className="h-3.5 w-3.5 mr-1" />Simpan Hasil</Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardContent className="py-4 flex gap-8 flex-wrap text-sm" data-testid="package-totals">
              <div>Total Good: <b className="font-mono text-emerald-700">{num(totalGood)} dus</b></div>
              <div>Total Reject: <b className="font-mono text-rose-700">{num(totalReject)} dus</b></div>
              <div>Target: <b className="font-mono">{num(detail.target_dus)} dus</b></div>
              <div>Estimasi Upah: <b className="font-mono">{rupiah(totalGood * detail.wage_rate)}</b></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-display text-base">Alokasi Bahan Teoritis per Paket (internal — bukan transaksi gudang)</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-package-allocation">
                <thead><tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-1 pr-3">Bahan</th><th className="text-right pr-3">Per Dus</th>
                  {detail.packages.map((p) => <th key={p.package_id} className="text-right pr-3">Paket #{p.seq}<div className="font-normal text-[10px]">{p.employee_name} ({p.target_dus} dus)</div></th>)}
                  <th className="text-right">Total</th>
                </tr></thead>
                <tbody>
                  {detail.materials.map((m) => (
                    <tr key={m.product_id} className="border-b border-slate-100">
                      <td className="py-1.5 pr-3">{m.sku} · {m.name}</td>
                      <td className="text-right font-mono pr-3">{num(m.qty_per_dus)} {m.unit}</td>
                      {(m.per_package || []).map((q, i) => <td key={i} className="text-right font-mono pr-3">{num(q)}</td>)}
                      <td className="text-right font-mono font-bold">{num(m.theoretical_qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
